/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

export interface UserStatus {
  isAuthenticated: boolean;
  user?: {
    email: string;
    id: number;
    username: string;
    avatar: string;
    onboarding?: boolean;
  };
}

export type AuthStatusKind = "authenticated" | "unauthorized" | "transient_failure";

export type AuthStatusResult =
  | { kind: "authenticated"; userStatus: UserStatus }
  | { kind: "unauthorized" }
  | { kind: "transient_failure" };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function isTransientHttpStatus(status: number): boolean {
  return status >= 500 || status === 408 || status === 429;
}

async function fetchAuthStatusOnce(
  signal?: AbortSignal,
): Promise<AuthStatusResult> {
  let res: Response;
  try {
    res = await fetch("/api/auth/status", { credentials: "include", signal });
  } catch {
    return { kind: "transient_failure" };
  }

  if (res.status === 401 || res.status === 403) {
    return { kind: "unauthorized" };
  }

  if (!res.ok) {
    if (isTransientHttpStatus(res.status)) {
      return { kind: "transient_failure" };
    }
    // Other 4xx on this endpoint: treat as transient to avoid false logout
    return { kind: "transient_failure" };
  }

  try {
    const userStatus = (await res.json()) as UserStatus;
    return { kind: "authenticated", userStatus };
  } catch {
    return { kind: "transient_failure" };
  }
}

const DEFAULT_TRANSIENT_ATTEMPTS = 3;
const RETRY_DELAY_MS = 120;

/**
 * Fetches /api/auth/status with short retries on transient errors.
 * Does not conflate network/5xx with guest session (401/403).
 */
export async function fetchAuthStatus(options?: {
  postLoginConfirm?: boolean;
  signal?: AbortSignal;
}): Promise<AuthStatusResult> {
  const postLogin = options?.postLoginConfirm ?? false;
  const signal = options?.signal;

  for (let attempt = 0; attempt < DEFAULT_TRANSIENT_ATTEMPTS; attempt++) {
    let result = await fetchAuthStatusOnce(signal);

    if (result.kind === "unauthorized" && postLogin) {
      await sleep(RETRY_DELAY_MS * 2);
      result = await fetchAuthStatusOnce(signal);
    }

    if (result.kind !== "transient_failure") {
      return result;
    }

    if (attempt < DEFAULT_TRANSIENT_ATTEMPTS - 1) {
      await sleep(RETRY_DELAY_MS * (attempt + 1));
    }
  }

  return { kind: "transient_failure" };
}

export function userDataToUserStatus(
  userData: Record<string, unknown>,
): UserStatus {
  const id = Number(userData.id ?? userData.user_id ?? 0);
  const email = String(userData.email ?? "");
  const rawUsername =
    userData.username ??
    userData.name ??
    userData.given_name ??
    (email ? email.split("@")[0] : "");
  const username = String(rawUsername || "User");
  const avatar = String(userData.avatar ?? userData.picture ?? "");
  return {
    isAuthenticated: true,
    user: { id, email, username, avatar },
  };
}

export function emailLoginToUserStatus(
  email: string,
  userId: number,
): UserStatus {
  const local = email ? email.split("@")[0] : "User";
  return {
    isAuthenticated: true,
    user: {
      id: userId,
      email,
      username: local || "User",
      avatar: "",
    },
  };
}

/**
 * After a successful login API call, confirm session; on transient/401-after-retry,
 * keep optimistic user info so the UI does not snap back to guest.
 */
export async function resolvePostLoginUserStatus(
  optimistic: UserStatus,
  options?: { signal?: AbortSignal },
): Promise<UserStatus> {
  const r = await fetchAuthStatus({
    postLoginConfirm: true,
    signal: options?.signal,
  });
  if (r.kind === "authenticated") {
    return r.userStatus;
  }
  return optimistic;
}
